import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { renderFormattedText } from "../../utils/mentionFormatter";
import { tiempoRelativo } from "../../utils/fechas";
import {
  FavoriteBorder,
  Favorite,
  ChatBubbleOutline,
  MoreVert
} from "@mui/icons-material";
import {
  Menu,
  MenuItem,
  IconButton,
  CircularProgress
} from "@mui/material";
import { authHeader } from "../../utils/authHeader";
import { puedeAdministrarEspacio } from "../../utils/permisosEspacio";
import "./post.scss";

import { API_BASE_URL, urlDeArchivo } from "../../config";
import Lightbox from "../lightbox/Lightbox";
const DEFAULT_PROFILE = "/assets/profileCover/DefaultProfile.jpg";

// El cálculo de "hace X" vive ahora en utils/fechas.js, junto con el resto
// de las fechas de la plataforma: así una publicación y un movimiento de
// puntos hechos en el mismo minuto muestran el mismo minuto. Lo que había
// acá devolvía la fecha de HOY cuando no sabía leer el valor, de modo que una
// fecha dañada se veía como "hace 0 segundos" en vez de delatarse.

const isVideo = url =>
  typeof url === "string" &&
  ["mp4", "mov", "avi", "webm", "mkv"].includes(url.split(".").pop().toLowerCase());

export default function Post({ post, onHashtagClick }) {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  // Administrador, o encargado del espacio donde está esta publicación: en
  // los dos casos puede moderarla (y sus comentarios) aunque no sea suya.
  const isAdmin = puedeAdministrarEspacio(currentUser, post.spaceId);

  // Al tocar una mención se abre el perfil de esa persona. El regex de
  // menciones puede capturar "@nombre apellido", así que se usa solo el
  // primer token como username; si no existe, la página de perfil muestra
  // su mensaje de "perfil no encontrado" en vez de romperse.
  const irAlPerfilMencionado = (mencion) => {
    const usuario = (mencion || "").trim().split(/s+/)[0];
    if (usuario) navigate(`/profile/${encodeURIComponent(usuario)}`);
  };

  const mediaList = Array.isArray(post.media)
    ? post.media.map(m => (typeof m === "string" ? m : m.url || "")).filter(Boolean)
    : [];

  const [idx, setIdx] = useState(0);
  // Las fotos del muro no se podían ampliar: se veían solo al tamaño de la
  // tarjeta. Al hacer clic se abren en el visor a pantalla completa, ya
  // situado en la foto que se tocó.
  const [visorAbierto, setVisorAbierto] = useState(false);
  const prev = () => setIdx(i => (i === 0 ? mediaList.length - 1 : i - 1));
  const next = () => setIdx(i => (i === mediaList.length - 1 ? 0 : i + 1));

  const [isLiked, setIsLiked] = useState(false);
  const [likes, setLikes] = useState(post.likes || 0);

  useEffect(() => {
    if (!currentUser) return;
    fetch(`${API_BASE_URL}/api/posts/${post.id}/isLiked`, {
      headers: { ...authHeader() },
    })
      .then(r => r.json())
      .then(d => setIsLiked(d.isLiked))
      .catch(console.error);
  }, [post.id, currentUser]);

  const toggleLike = async () => {
    if (!currentUser) return;
    const action = isLiked ? "unlike" : "like";
    try {
      const res = await fetch(`${API_BASE_URL}/api/posts/${post.id}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ uid: currentUser.id })
      });
      if (res.ok) {
        const { likes: cnt } = await res.json();
        setLikes(cnt);
        setIsLiked(!isLiked);
      }
    } catch (err) {
      console.error("Error al actualizar like:", err);
    }
  };

  const [showC, setShowC] = useState(false);
  const [comments, setComments] = useState(post.comments || []);
  const [newC, setNewC] = useState("");
  const [loading, setLoading] = useState(false);

  // Edición en línea de un comentario propio.
  const [comentarioEditando, setComentarioEditando] = useState(null);
  const [textoEditado, setTextoEditado] = useState("");
  const [guardandoComentario, setGuardandoComentario] = useState(false);

  // Se puede gestionar el comentario propio; un administrador o el encargado
  // del espacio, cualquiera.
  const puedeGestionarComentario = (c) =>
    isAdmin || String(c.userId) === String(currentUser?.id);

  const empezarEdicion = (c) => {
    setComentarioEditando(c.id);
    setTextoEditado(c.body || "");
  };

  const cancelarEdicion = () => {
    setComentarioEditando(null);
    setTextoEditado("");
  };

  const guardarComentario = async (comentarioId) => {
    const texto = textoEditado.trim();
    if (!texto) return;

    setGuardandoComentario(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/comments/${comentarioId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ body: texto }),
      });
      if (res.ok) {
        cancelarEdicion();
        await fetchComments();
      } else {
        const { error } = await res.json().catch(() => ({}));
        alert(error || "No se pudo editar el comentario");
      }
    } catch (err) {
      console.error("Error al editar comentario:", err);
      alert("Error de red al editar el comentario");
    } finally {
      setGuardandoComentario(false);
    }
  };

  const eliminarComentario = async (comentarioId) => {
    if (!window.confirm("¿Eliminar este comentario?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/comments/${comentarioId}`, {
        method: "DELETE",
        headers: { ...authHeader() },
      });
      if (res.ok) {
        await fetchComments();
      } else {
        const { error } = await res.json().catch(() => ({}));
        alert(error || "No se pudo eliminar el comentario");
      }
    } catch (err) {
      console.error("Error al eliminar comentario:", err);
      alert("Error de red al eliminar el comentario");
    }
  };

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/comments?postId=${post.id}`, {
        headers: { ...authHeader() },
      });
      if (res.ok) {
        const { comments } = await res.json();
        setComments(comments);
      }
    } catch (err) {
      console.error("Error al obtener comentarios:", err);
    }
  };

  useEffect(() => {
    if (showC) fetchComments();
  }, [showC]);

  const publishComment = async () => {
    if (!newC.trim() || !currentUser) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({
          postId: post.id,
          userId: currentUser.id,
          body: newC,
          date: new Date().toISOString(),
          likes: 0
        })
      });
      if (res.ok) {
        setNewC("");
        await fetchComments();
      }
    } catch (err) {
      console.error("Error al publicar comentario:", err);
    } finally {
      setLoading(false);
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = e => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const isOwner = String(post.userId) === String(currentUser?.id);
  const canManagePost = isAdmin || isOwner;

  const [showEditModal, setShowEditModal] = useState(false);
  const [editBody, setEditBody] = useState(post.body || "");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");
  const [currentBody, setCurrentBody] = useState(post.body || "");

  const openEditModal = () => {
    closeMenu();
    setEditBody(currentBody);
    setEditError("");
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    if (!editBody.trim()) {
      setEditError("La publicación no puede quedar vacía");
      return;
    }
    setSavingEdit(true);
    setEditError("");
    try {
      const fd = new FormData();
      fd.append("title", post.title || "");
      fd.append("body", editBody.trim());
      const res = await fetch(`${API_BASE_URL}/api/posts/${post.id}`, {
        method: "PUT",
        headers: { ...authHeader() },
        body: fd
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || "No se pudo guardar la publicación");
      }
      setCurrentBody(editBody.trim());
      setShowEditModal(false);
    } catch (err) {
      setEditError(err.message || "Error al guardar los cambios");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    closeMenu();
    if (!window.confirm("¿Eliminar esta publicación?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/posts/${post.id}`, {
        method: "DELETE",
        headers: { ...authHeader() }
      });
      if (res.ok) {
        window.location.reload();
      } else {
        const error = await res.json();
        alert("Error al eliminar: " + (error.error || "desconocido"));
      }
    } catch (err) {
      console.error("Error al eliminar publicación:", err);
      alert("Error de red al eliminar publicación");
    }
  };

  const date = tiempoRelativo(post.date);

  return (
    <div className="post">
      <div className="post-wrapper">
        <div className="post-header">
          {/* Foto y nombre del autor llevan a su perfil, como en cualquier
              red social. Si la publicación no trae username (datos viejos),
              se pinta igual pero sin enlace. */}
          {post.userUsername ? (
            <Link to={`/profile/${post.userUsername}`} className="author-link">
              <img
                className="profile-img"
                src={post.userProfilePicture || DEFAULT_PROFILE}
                alt={post.userName}
                onError={e => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_PROFILE;
                }}
              />
            </Link>
          ) : (
            <img
              className="profile-img"
              src={post.userProfilePicture || DEFAULT_PROFILE}
              alt={post.userName}
              onError={e => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_PROFILE;
              }}
            />
          )}

          <div className="author-info">
            {post.userUsername ? (
              <Link to={`/profile/${post.userUsername}`} className="author-link">
                <span className="name">{post.userName}</span>
              </Link>
            ) : (
              <span className="name">{post.userName}</span>
            )}
            {post.spaceName && (
              <span className="space-name">En: {post.spaceName}</span>
            )}
            <span className="post-date">{date}</span>
          </div>

          {canManagePost && (
            <>
              <IconButton onClick={openMenu} size="small">
                <MoreVert fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={closeMenu}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right"
                }}
              >
                <MenuItem onClick={openEditModal}>
                  Modificar publicación
                </MenuItem>
                <MenuItem onClick={handleDelete} className="delete-item">
                  Eliminar publicación
                </MenuItem>
              </Menu>
            </>
          )}
        </div>

        {/* CONTENIDO */}
        <div className="post-content">
          {post.title && <h2 className="title">{post.title}</h2>}

          {currentBody && (
            <p className="body">
              {renderFormattedText(currentBody, {
                onHashtagClick,
                onMentionClick: irAlPerfilMencionado,
              })}
            </p>
          )}

          {mediaList.length > 0 && (
            <div className="post-images">
              {mediaList.length > 1 && (
                <>
                  <button className="nav prev" onClick={prev}>
                    &lt;
                  </button>
                  <button className="nav next" onClick={next}>
                    &gt;
                  </button>
                </>
              )}

              {isVideo(mediaList[idx]) ? (
                <video
                  className="carousel-img"
                  src={urlDeArchivo(mediaList[idx])}
                  controls
                  controlsList="nodownload"
                />
              ) : (
                <img
                  className="carousel-img"
                  src={urlDeArchivo(mediaList[idx])}
                  alt={`media-${idx}`}
                  onClick={() => setVisorAbierto(true)}
                  title="Ver en grande"
                />
              )}

              <div className="image-counter">
                {idx + 1} / {mediaList.length}
              </div>

              {visorAbierto && (
                <Lightbox
                  items={mediaList}
                  indice={idx}
                  onIndice={setIdx}
                  onCerrar={() => setVisorAbierto(false)}
                />
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="post-footer">
          <div className="actions">
            <div className="action" onClick={toggleLike}>
              {isLiked ? <Favorite htmlColor="red" /> : <FavoriteBorder />} Me
              gusta
            </div>
            <div className="action" onClick={() => setShowC(v => !v)}>
              <ChatBubbleOutline /> Comentarios
            </div>
          </div>
          <div className="details">
            {likes} me gusta · {comments.length} comentarios
          </div>
        </div>

        {/* COMENTARIOS */}
        {showC && (
          <div className="comments-section">
            {loading && (
              <div className="comment-loading">
                <CircularProgress size={24} />
              </div>
            )}

            <div className="comment-input-row">
              <input
                className="comment-input"
                value={newC}
                onChange={e => setNewC(e.target.value)}
                placeholder="Escribe un comentario..."
              />
              <button className="comment-button" onClick={publishComment}>
                Publicar
              </button>
            </div>

            <div className="comment-list">
              {comments.map(c => (
                <div className="comment" key={c.id}>
                  <img
                    className="comment-profile"
                    src={urlDeArchivo(c.profilePicture) || DEFAULT_PROFILE}
                    alt={c.username}
                    onError={e => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_PROFILE;
                    }}
                  />
                  <div className="comment-data">
                    {comentarioEditando === c.id ? (
                      <div className="comment-edit">
                        <textarea
                          className="comment-edit-input"
                          value={textoEditado}
                          onChange={(e) => setTextoEditado(e.target.value)}
                          rows={2}
                          autoFocus
                        />
                        <div className="comment-edit-actions">
                          <button
                            className="comment-save"
                            onClick={() => guardarComentario(c.id)}
                            disabled={guardandoComentario || !textoEditado.trim()}
                          >
                            {guardandoComentario ? "Guardando…" : "Guardar"}
                          </button>
                          <button
                            className="comment-cancel"
                            onClick={cancelarEdicion}
                            disabled={guardandoComentario}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="comment-body">{c.body}</p>
                    )}

                    <span className="comment-username">@{c.username}</span>
                    <span className="comment-date">
                      {tiempoRelativo(c.date)}
                    </span>

                    {puedeGestionarComentario(c) && comentarioEditando !== c.id && (
                      <span className="comment-actions">
                        <button className="comment-action" onClick={() => empezarEdicion(c)}>
                          Editar
                        </button>
                        <button
                          className="comment-action delete"
                          onClick={() => eliminarComentario(c.id)}
                        >
                          Eliminar
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MODIFICAR PUBLICACIÓN */}
        {showEditModal && (
          <div className="edit-post-overlay" onClick={() => !savingEdit && setShowEditModal(false)}>
            <div className="edit-post-modal" onClick={e => e.stopPropagation()}>
              <div className="edit-post-header">
                <h3>Modificar publicación</h3>
                <button
                  type="button"
                  className="edit-post-close"
                  onClick={() => setShowEditModal(false)}
                  disabled={savingEdit}
                  aria-label="Cerrar"
                >
                  ✕
                </button>
              </div>
              <textarea
                className="edit-post-textarea"
                value={editBody}
                onChange={e => setEditBody(e.target.value)}
                rows={5}
                maxLength={3000}
                autoFocus
                disabled={savingEdit}
              />
              {editError && <p className="edit-post-error">{editError}</p>}
              <div className="edit-post-actions">
                <button
                  type="button"
                  className="edit-post-cancel"
                  onClick={() => setShowEditModal(false)}
                  disabled={savingEdit}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="edit-post-save"
                  onClick={saveEdit}
                  disabled={savingEdit}
                >
                  {savingEdit ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
